import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSpring } from 'react-spring';
import ReviewCard from '../../components/Review/Card/ReviewCard';
import ReviewCardSkeleton from '../../components/Review/Card/ReviewCardSkelton';
import { FlexboxColumn } from '../../components/action/Flexbox';
import Grid from '../../components/action/Grid';
import { Line } from '../../components/action/Line';
import { useBackendApi } from '../../helpers/backendHelper';
import './UserReview.scss';

const UserReview = () => {
  const springProps = useSpring({
    from: { transform: 'translateY(-25px)', opacity: 0 },
    to: { transform: 'translateY(0)', opacity: 1 },
    config: { tension: 200, friction: 20 },
  });
  const { userAddress } = useParams();
  const backendHelper = useBackendApi();

  const [mainFeed, setMainFeed] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pagesLoaded = useRef(new Set());

  const loaderRef = useRef(null);
  const observerRef = useRef(null);

  const getMainFeed = useCallback(
    async (body = {}, currentPage = 1, reset = false) => {
      if (loading || pagesLoaded.current.has(currentPage)) return;

      setLoading(true);
      try {
        const response = await backendHelper.reviews.getFeed({
          filter: {
            ...body,
            address: userAddress,
            pagination: {
              page: currentPage,
              limit: 5,
            },
          },
        });

        const fetchedFeed = response?.data || [];

        if (reset) {
          setMainFeed(fetchedFeed);
        } else {
          setMainFeed(prev => [...prev, ...fetchedFeed]);
        }

        pagesLoaded.current.add(currentPage);

        if (fetchedFeed.length === 0) {
          setHasMore(false);
        }
      } catch (err) {
        console.error('Error fetching feed:', err);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [backendHelper, loading],
  );

  useEffect(() => {
    let body = {};
    if (userAddress) {
      body['address'] = userAddress;
      getMainFeed(body, page, page === 1);
    }
  }, [page, getMainFeed, userAddress]);

  // Intersection Observer Setup
  useEffect(() => {
    if (loading || !hasMore) return;

    const currentLoader = loaderRef.current;
    if (!currentLoader) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          setPage(prev => prev + 1);
        }
      },
      {
        rootMargin: '200px',
      },
    );

    observer.observe(currentLoader);
    observerRef.current = observer;

    return () => {
      if (observerRef.current && currentLoader) {
        observerRef.current.unobserve(currentLoader);
      }
    };
  }, [loading, hasMore]);

  return (
    <FlexboxColumn alignItems='flex-start' fw justifyContent='flex-start'>
      <FlexboxColumn className='feed_wrapper' fw justifyContent='flex-start' alignItems='flex-start'>
        <Grid style={springProps} columns={1} gap='2rem' responsive={false}>
          {mainFeed.length === 0 && loading && Array.from({ length: 4 }).map((_, i) => <ReviewCardSkeleton key={i} />)}

          {mainFeed.map(feed => (
            <>
              <ReviewCard eligibleProtocols={[]} key={feed.postId} data={feed} maxWidth={true} showNoOfReviews={false} userReview={true} />
              <Line />
            </>
          ))}

          {loading && <ReviewCardSkeleton />}
          {!loading && hasMore && (
            <div ref={loaderRef} className=''>
              <span className='invite_wrapper_loader'></span>
            </div>
          )}
        </Grid>
        {!hasMore && <div style={{ textAlign: 'center', padding: '1rem' }}>No more reviews to show.</div>}
      </FlexboxColumn>
    </FlexboxColumn>
  );
};

export default UserReview;
